import dynamic from 'next/dynamic';
import { Box, Button, Flex, Text, useDisclosure } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { EventPost, EventPrice, Site, Translate } from '../../types';
import { resolveFields } from '../Fields';
import ArticleContainer from '../ArticleContainer';
import Breadcrumb from '../Breadcrumb';
import TeaserMeta, { TeaserMetaField } from '../TeaserMeta';
import {
  formatDate,
  formatHour,
  isMultipleDays,
  createDate,
} from '../../lib/utils.date';
import HeroSplit from '../HeroSplit';
import { useBlog, useTranslation } from '../../lib/hooks.context';
import EventTeasers from '../Fields/EventTeasers';
import { mapNewsTeaserProps } from '../../lib/resolvers';
import {
  ContentColumn as Column,
  ContentColumns,
  MetaContent,
} from '../BasicBlocks';
import { splitFields } from './Page';
import Heading from '../Heading';
import {
  formatAddressFromLocation as formatAddress,
  formatLocation,
} from '../../lib/utils';
import { BlockRenderer } from '../Fields/BlockRenderer';

const Teaser = dynamic(() => import('../Teaser'));
const EventModal = dynamic(() => import('../EventModal'));
const StaticMap = dynamic(() => import('../StaticMap'));

const getEventDate = (
  start_time: string,
  end_time: null | false | string,
  t: Translate,
  locale: string,
) => {
  const start = createDate(start_time);
  const startHour = formatHour(start);
  const end = end_time ? createDate(end_time) : undefined;
  const endHour = formatHour(end);

  if (end) {
    if (isMultipleDays(start, end)) {
      const showStartYear = start.getFullYear() !== end.getFullYear();
      const displayTime =
        startHour === '00.00' && endHour === '00.00'
          ? ''
          : `, ${t('o_clock')} ${startHour} - ${endHour}`;
      return `${formatDate(start, locale, showStartYear)} - ${formatDate(
        end,
        locale,
        true,
      )}${displayTime}`;
    }
    if (startHour === '00.00' && endHour === '00.00') {
      return `${formatDate(start, locale, true)}, ${t(
        'event_whole_day',
      ).toLowerCase()}`;
    }
    return `${formatDate(start, locale, true)}, ${t(
      'o_clock',
    )} ${startHour} - ${endHour}`;
  }

  if (startHour === '00.00') {
    return `${formatDate(start, locale, true)} ${t(
      'event_whole_day',
    ).toLowerCase()}`;
  }

  if (startHour) {
    return `${formatDate(start, locale, true)}, ${t('o_clock')} ${t(
      'event_start_hour',
      {
        hour: startHour,
      },
    )}`;
  }

  return `${formatDate(start, locale)}`;
};

const getRegistrationEndDate = (time: string, t: Translate, locale: string) => {
  const regEndDate = createDate(time);
  const regEndHour = formatHour(regEndDate);
  const displayTime =
    regEndHour !== '00.00' ? `, ${t('o_clock')} ${regEndHour}` : '';
  const registrationDeadline = `${formatDate(
    regEndDate,
    locale,
    true,
  )}${displayTime}`;
  return t('event_registration_by', { date: registrationDeadline });
};

const isPastDate = (time: string) => createDate(time) < createDate();

const EventPrices = ({ price }: { price: EventPrice }) => {
  const t = useTranslation();

  return typeof price === 'string' ? (
    <TeaserMetaField title={t('event_price')}>{price}</TeaserMetaField>
  ) : (
    <>
      <TeaserMetaField title={t('event_price_members')}>
        {price.membership}
      </TeaserMetaField>
      <TeaserMetaField title={t('event_price_others')}>
        {price.others}
      </TeaserMetaField>
    </>
  );
};

const EventPage = ({
  fields,
  post_title: title,
  post_excerpt: leadText,
  featured_image: image,
  data: {
    next_events,
    form,
    start_time,
    end_time,
    registration_end_time,
    location,
    display_address: displayAddress,
    is_webinar,
    price,
    related,
    info,
  },
}: EventPost) => {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const site = useBlog(true) as Site;
  const [hero, contentFields] = resolveFields(fields);
  const { basicFields, sectionFields } = splitFields(contentFields);
  const t = useTranslation();

  const heroProps = hero
    ? {
        title: hero.heading?.title || title,
        leadText: hero.text || leadText,
        image: hero.image || image,
      }
    : { title, leadText, image };

  const regEndTime = registration_end_time || end_time || start_time;
  const isEventClosed = isPastDate(regEndTime);
  const { locale } = useRouter();

  return (
    <ArticleContainer>
      <Box>
        <Breadcrumb mb="4" />
        <HeroSplit data-test-id="event-hero" {...heroProps}>
          <TeaserMeta my="7">
            <TeaserMetaField title={t('event_date')}>
              {getEventDate(start_time, end_time, t, locale || 'fi')}
            </TeaserMetaField>
            {location || is_webinar ? (
              <TeaserMetaField title={t('event_location')}>
                {location ? formatLocation(location) : t('webinar')}
              </TeaserMetaField>
            ) : null}
            {!price ? null : <EventPrices price={price} />}
            {(form || registration_end_time) && (
              <TeaserMetaField title={t('event_registration')}>
                {!isEventClosed
                  ? getRegistrationEndDate(regEndTime, t, locale || 'fi')
                  : t('event_registration_ended')}
              </TeaserMetaField>
            )}
          </TeaserMeta>
          {isEventClosed && form ? ( // eslint-disable-line no-nested-ternary
            <Button mb="2" variant="solidGreen" size="xl" disabled>
              {t('event_signup_over')}
            </Button>
          ) : form ? (
            <Button
              variant="solidGreen"
              onClick={onOpen}
              mb="2"
              size="xl"
              disabled={!form}
            >
              {t('event_signup')}
            </Button>
          ) : null}
        </HeroSplit>
      </Box>
      <ContentColumns>
        <Column>
          <Box>
            <BlockRenderer blocks={basicFields} />
          </Box>
          {related ? <Teaser {...mapNewsTeaserProps(related, t)} /> : null}
        </Column>
        <Column>
          <MetaContent>
            <Box>
              <Heading as="p" variant="h5" mb="2">
                {t('event_organizer')}
              </Heading>
              <Text>{site.blogname}</Text>
            </Box>
            {location ? (
              <Box>
                <Heading as="p" variant="h5" mb="2">
                  {t('event_location')}
                </Heading>
                <Flex flexDirection="column" mb="2">
                  {displayAddress && (
                    <Text
                      dangerouslySetInnerHTML={{ __html: displayAddress }}
                    />
                  )}
                  {!displayAddress && location && (
                    <Text>{formatAddress(location)}</Text>
                  )}
                </Flex>
              </Box>
            ) : null}
            {info ? (
              <Box>
                <Heading as="p" variant="h5" mb="2">
                  {t('event_additional_info')}
                </Heading>
                <Text>{info}</Text>
              </Box>
            ) : null}
          </MetaContent>
          {location ? <StaticMap {...location} /> : null}
        </Column>
      </ContentColumns>
      {next_events?.data?.length ? (
        <EventTeasers {...next_events} mb="10" />
      ) : null}
      <BlockRenderer blocks={sectionFields} />
      {!isEventClosed && form ? (
        <EventModal
          form={form}
          isOpen={isOpen}
          onClose={onClose}
          title={t('event_signup')}
        />
      ) : null}
    </ArticleContainer>
  );
};

export default EventPage;
